import { useState, useRef } from 'react';
import { X, Plus, Search, Tag, ChevronRight, Archive, ArchiveRestore } from 'lucide-react';
import { EmptyState, Skeleton } from '../../components/boothly/index.js';
import { useToast } from '../../components/boothly/toast-provider.jsx';
import { useConfirm } from '../../components/boothly/confirm-provider.jsx';
import { fmtCurrency } from '../../lib/format.js';
import { ProductSheet } from './product-sheet.jsx';

// ── CatalogSheet ────────────────────────────────────────────────────────────────
// Produktkatalog im Online-Store-v2-Glas-Look. Prominenter „Neues Produkt"-Button,
// Aktiv/Archiviert-Segment, Suche, Produktzeilen (Monogramm + Name + Preis + Chevron).
// Archivieren per Wisch-nach-links auf der Zeile ODER im Produkt-Detail. KEIN Loeschen,
// KEIN Bild, KEIN Lager-Badge, KEINE Kanal-Chips in der Liste. Nur owner/admin (manage_catalog).

const SWIPE_ACTION_W = 88; // px, Breite der freigewischten Archivieren-Aktion

export function CatalogSheet({ open, onOpenChange, store, currentRole, dark, loading }) {
  const canManage = ['owner', 'admin'].includes(currentRole);
  const toast = useToast();
  const confirm = useConfirm();
  const [tab, setTab] = useState('aktiv'); // 'aktiv' | 'archiviert'
  const [query, setQuery] = useState('');
  // null = Produkt-Sheet zu · 'new' = Anlegen · Produkt-Objekt = Bearbeiten
  const [editing, setEditing] = useState(null);

  const products = store.products || [];
  const q = query.trim().toLowerCase();
  const visible = products
    .filter(p => (tab === 'archiviert' ? !!p.archivedAt : !p.archivedAt))
    .filter(p => !q || (p.name || '').toLowerCase().includes(q));

  const handleArchive = async (p) => {
    const ok = await confirm({
      title: 'Produkt archivieren?',
      subtitle: `„${p.name}" verschwindet aus Katalog und Produktauswahl. Bestehende Bestellungen bleiben erhalten.`,
      confirmLabel: 'Archivieren',
      destructive: true,
    });
    if (!ok) return;
    const updated = await store.apiProductArchive(p.id, true);
    if (updated) {
      store.setProducts(prev => prev.map(x => (x.id === p.id ? updated : x)));
      toast.success('Produkt archiviert');
    }
  };

  const handleRestore = async (p) => {
    const updated = await store.apiProductArchive(p.id, false);
    if (updated) {
      store.setProducts(prev => prev.map(x => (x.id === p.id ? updated : x)));
      toast.success('Produkt wiederhergestellt');
    }
  };

  return (
    <>
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[20px]"
            style={{ animation: 'fadeIn 0.2s ease' }}
            onClick={() => onOpenChange(false)}
          />

          {/* Sheet */}
          <div
            data-testid="sheet-catalog"
            className="fixed inset-x-0 bottom-0 z-50 flex max-h-[92vh] flex-col rounded-t-3xl"
            style={{
              paddingBottom: 'env(safe-area-inset-bottom)',
              animation: 'slideUpSheet 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
              background: dark ? '#2C2C2E' : '#FFFFFF',
            }}
          >
            <div className="flex justify-center pt-3 pb-2">
              <div className="bottom-sheet-handle" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-4">
              <h2 className="text-xl font-semibold">Katalog</h2>
              <button onClick={() => onOpenChange(false)} className="ghost-icon-btn">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-4">
              {/* Neues Produkt — prominenter Akzent-Button */}
              {canManage && (
                <button
                  onClick={() => setEditing('new')}
                  data-testid="catalog-new-product-btn"
                  className="w-full flex items-center justify-center gap-2 rounded-full py-3.5 font-semibold text-white transition-transform active:scale-[0.98]"
                  style={{ background: 'var(--accent-online)' }}
                >
                  <Plus className="h-5 w-5" />
                  Neues Produkt
                </button>
              )}

              {/* Aktiv / Archiviert — Segmented */}
              <div className="flex rounded-lg p-0.5" style={{ background: 'var(--fill-quaternary)' }}>
                {[['aktiv', 'Aktiv'], ['archiviert', 'Archiviert']].map(([v, l]) => {
                  const on = tab === v;
                  return (
                    <button
                      key={v}
                      data-testid={`catalog-segment-${v}`}
                      onClick={() => setTab(v)}
                      className="flex-1 rounded-md py-1.5 text-[13px] transition-colors"
                      style={{
                        fontWeight: on ? 600 : 500,
                        color: on ? 'var(--label-primary)' : 'var(--label-secondary)',
                        background: on ? 'var(--bg-L1)' : 'transparent',
                        border: on ? '1px solid var(--border-default)' : '1px solid transparent',
                      }}
                    >
                      {l}
                    </button>
                  );
                })}
              </div>

              {/* Suche */}
              <div className="glass-card flex items-center gap-3 rounded-xl px-4 py-3">
                <Search className="h-5 w-5 text-muted-foreground" />
                <input
                  data-testid="catalog-search"
                  type="text"
                  placeholder="Produkt suchen..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>

              {/* Produktliste */}
              {loading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 rounded-2xl" />
                  ))}
                </div>
              ) : visible.length === 0 ? (
                q ? (
                  <EmptyState
                    icon={Search}
                    title="Keine Treffer"
                    description="Kein Produkt passt zu deiner Suche."
                  />
                ) : tab === 'archiviert' ? (
                  <EmptyState
                    icon={Archive}
                    title="Keine archivierten Produkte"
                    description="Archivierte Produkte erscheinen hier."
                  />
                ) : (
                  <EmptyState
                    icon={Tag}
                    title="Noch keine Produkte"
                    description="Lege Produkte an, um Preise und Kosten je Produkt zu pflegen."
                    cta={canManage ? { label: 'Produkt anlegen', accent: 'online', onClick: () => setEditing('new') } : undefined}
                  />
                )
              ) : (
                <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border-subtle)' }}>
                  {visible.map((p, idx) => (
                    <ProductRow
                      key={p.id}
                      p={p}
                      idx={idx}
                      archived={tab === 'archiviert'}
                      canManage={canManage}
                      onOpen={() => setEditing(p)}
                      onArchive={() => handleArchive(p)}
                      onRestore={() => handleRestore(p)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Produkt-Sheet (Anlegen / Bearbeiten) — rendert über dem Katalog */}
          <ProductSheet
            open={editing != null}
            onOpenChange={(v) => { if (!v) setEditing(null); }}
            store={store}
            currentRole={currentRole}
            dark={dark}
            product={editing === 'new' ? null : editing}
          />
        </>
      )}
    </>
  );
}

// ── ProductRow ──────────────────────────────────────────────────────────────────
// Eine Katalog-Zeile: Monogramm + Name + Preis + Chevron. Aktive Zeilen lassen sich
// nach links wischen, um die Archivieren-Aktion freizulegen (nur canManage). Archivierte
// Zeilen zeigen stattdessen eine „Wiederherstellen"-Aktion. Tap (ohne Drag) öffnet das Detail.
function ProductRow({ p, idx, archived, canManage, onOpen, onArchive, onRestore }) {
  const swipeEnabled = !archived && canManage;
  const [tx, setTx] = useState(0);
  const [animating, setAnimating] = useState(false);
  // Gesten-State (nicht reaktiv): Startpunkt, Richtungs-Lock, Offset bei Touch-Start
  const g = useRef({ x0: 0, y0: 0, dir: null, openAt: 0 });

  const monogram = (p.name?.trim()?.[0] || '?').toUpperCase();

  const onTouchStart = (e) => {
    const t = e.touches[0];
    g.current = { x0: t.clientX, y0: t.clientY, dir: null, openAt: tx };
    setAnimating(false);
  };

  const onTouchMove = (e) => {
    const t = e.touches[0];
    const dx = t.clientX - g.current.x0;
    const dy = t.clientY - g.current.y0;
    // Richtungs-Lock: erst ab dominanter Bewegung festlegen (Mindestweg 8px)
    if (g.current.dir === null) {
      if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) g.current.dir = 'h';
      else if (Math.abs(dy) > 8 && Math.abs(dy) >= Math.abs(dx)) g.current.dir = 'v';
    }
    if (g.current.dir === 'h') {
      e.preventDefault(); // horizontal = wir; vertikal bleibt beim Sheet-Scroll
      const next = Math.max(-SWIPE_ACTION_W, Math.min(0, g.current.openAt + dx));
      setTx(next);
    }
  };

  const onTouchEnd = () => {
    if (g.current.dir !== 'h') return;
    setAnimating(true);
    // Über die halbe Aktionsbreite gewischt → offen einrasten, sonst zurück
    setTx(tx < -SWIPE_ACTION_W / 2 ? -SWIPE_ACTION_W : 0);
  };

  const closeSwipe = () => { setAnimating(true); setTx(0); };

  const handleRowClick = () => {
    if (g.current.dir === 'h') return; // war ein Swipe, kein Tap
    if (tx !== 0) { closeSwipe(); return; } // offen → erst schließen
    onOpen();
  };

  const rowSurface = { background: 'var(--bg-L1)' };
  const dividerTop = idx > 0 ? { borderTop: '1px solid var(--border-subtle)' } : null;

  return (
    <div className="relative overflow-hidden" style={{ touchAction: swipeEnabled ? 'pan-y' : 'auto' }}>
      {/* Freigewischte Aktion hinter der Zeile (rechts) */}
      {swipeEnabled && (
        <button
          type="button"
          data-testid={`catalog-swipe-archive-${p.id}`}
          aria-label="Produkt archivieren"
          onClick={() => { onArchive(); closeSwipe(); }}
          className="absolute inset-y-0 right-0 flex flex-col items-center justify-center gap-0.5"
          style={{
            width: SWIPE_ACTION_W,
            background: 'color-mix(in srgb, var(--accent-orange-strong) 16%, transparent)',
            color: 'var(--accent-orange-strong)',
          }}
        >
          <Archive className="h-5 w-5" />
          <span className="text-[11px] font-semibold">Archivieren</span>
        </button>
      )}

      {/* Zeileninhalt (verschiebt sich beim Wischen) */}
      <div
        data-testid={`catalog-product-${p.id}`}
        onClick={handleRowClick}
        onTouchStart={swipeEnabled ? onTouchStart : undefined}
        onTouchMove={swipeEnabled ? onTouchMove : undefined}
        onTouchEnd={swipeEnabled ? onTouchEnd : undefined}
        className="relative flex items-center gap-3 px-4 py-3 cursor-pointer active:opacity-90"
        style={{
          ...rowSurface,
          ...dividerTop,
          transform: `translateX(${tx}px)`,
          transition: animating ? 'transform 200ms cubic-bezier(0.32, 0.72, 0, 1)' : 'none',
        }}
      >
        {/* Monogramm-Platzhalter */}
        <div
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-[15px] font-semibold"
          style={{ background: 'var(--tint-online)', color: 'var(--accent-online)' }}
        >
          {monogram}
        </div>

        {/* Name + Preis */}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-medium">{p.name}</div>
          <div className="text-[13px] text-[var(--label-tertiary)] mt-px">{fmtCurrency(p.price)}</div>
        </div>

        {/* Rechts: Wiederherstellen (archiviert) ODER Chevron */}
        {archived && canManage ? (
          <button
            type="button"
            data-testid={`catalog-restore-${p.id}`}
            onClick={(e) => { e.stopPropagation(); onRestore(); }}
            className="flex flex-shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold active:opacity-80"
            style={{ background: 'var(--tint-online)', color: 'var(--accent-online)' }}
          >
            <ArchiveRestore className="h-4 w-4" />
            Wiederherstellen
          </button>
        ) : (
          <ChevronRight size={18} className="flex-shrink-0 text-[var(--label-tertiary)]" />
        )}
      </div>
    </div>
  );
}
